/**
 * Перенос `lib/src/utils/sse/sse_client.dart`.
 *
 * В Dart поток открывал `package:http` (`http.Client.send` + подписка на
 * `response.stream`). В браузере вместо него `fetch`: штатный `EventSource`
 * умеет только GET и не умеет отправлять JSON-тело, а тело здесь есть всегда.
 * Поэтому ответ читается вручную — `response.body` через `TextDecoderStream`,
 * с делением на строки на своей стороне: кусок может оборваться посреди
 * строки, и остаток надо додержать до следующего куска.
 *
 * Семантика класса не менялась: те же колбэки, те же таймауты, те же причины
 * отказа и те же записи в журнале разработчика.
 */

import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';
import { fromRequestUrl, toRequestUrl } from '@/utils/proxyUrl';
import { parseSseEvent } from '@/utils/sse/sseEvent';
import type { SseEvent } from '@/utils/sse/sseEvent';
import {
  sseBadRequest,
  sseBroken,
  sseConnectTimeout,
  sseFailureToString,
  sseServerDown,
  sseSilent,
  sseUnreachable,
} from '@/utils/sse/sseFailure';
import type { SseFailure } from '@/utils/sse/sseFailure';

/** `Duration(seconds: 60)` из Dart. */
const DEFAULT_IDLE_TIMEOUT_MS = 60_000;

/** `Duration(seconds: 15)` из Dart. */
const DEFAULT_CONNECT_TIMEOUT_MS = 15_000;

const DEFAULT_METHOD = 'POST';

const MS_PER_SECOND = 1000;

/** Границы «успешного» ответа: `status < 200 || status >= 300` — отказ. */
const HTTP_OK_MIN = 200;
const HTTP_OK_MAX_EXCLUSIVE = 300;

/** С этого кода отказ считается падением сервера, а не отклонённым запросом. */
const HTTP_SERVER_ERROR_MIN = 500;

/** Аналог `dev.log('$message', name: 'SSE')` из `dart:developer`. */
function logSse(message: string): void {
  console.debug(`[SSE] ${message}`);
}

/** Именованные аргументы `SseConnection.open`. */
export interface SseConnectionOptions {
  /** В Dart здесь был `Uri`; строка и `URL` одинаково подходят. */
  readonly url: string | URL;
  readonly onEvent: (event: SseEvent) => void;
  readonly onFailure?: (failure: SseFailure) => void;
  readonly onDone?: () => void;
  readonly method?: string;
  /** Тело запроса; кодируется в JSON. `Object?` в Dart. */
  readonly body?: unknown;
  readonly headers?: Readonly<Record<string, string>>;
  /** `Duration` перенесён числом миллисекунд. */
  readonly idleTimeout?: number;
  readonly connectTimeout?: number;
}

/** Всё, что нужно для запроса: в Dart это были аргументы `_start`. */
interface SseRequest {
  readonly url: string;
  readonly method: string;
  readonly body: unknown;
  readonly headers: Readonly<Record<string, string>> | undefined;
}

interface SseConnectionInit {
  readonly onEvent: (event: SseEvent) => void;
  readonly onFailure: (failure: SseFailure) => void;
  readonly onDone: () => void;
  readonly idleTimeout: number;
  readonly connectTimeout: number;
}

/** Готовые строки из накопленного текста и незавершённый остаток. */
interface PendingLines {
  readonly lines: readonly string[];
  readonly rest: string;
}

/** Разделители строк, которые признаёт `LineSplitter` из `dart:convert`. */
const LINE_TERMINATOR = /\r\n|\n|\r/;

/**
 * Делит накопленный текст на завершённые строки и остаток.
 *
 * Одинокий `\r` в конце куска может оказаться началом `\r\n` из следующего
 * куска — он придерживается в остатке, иначе одна строка разъехалась бы на
 * две. `LineSplitter` в Dart делал то же самое, только внутри себя.
 */
function takeLines(buffered: string): PendingLines {
  const heldAt = buffered.endsWith('\r') ? buffered.length - 1 : buffered.length;
  const head = buffered.slice(0, heldAt);
  const held = buffered.slice(heldAt);

  const parts = head.split(LINE_TERMINATOR);
  return {
    lines: parts.slice(0, -1),
    rest: `${parts[parts.length - 1]}${held}`,
  };
}

/**
 * Хвост после конца потока: `LineSplitter` отдавал незавершённую строку как
 * последнюю. В остатке всегда не больше одной строки — придержанный `\r` в
 * его конце завершает её, а не делит.
 */
function flushLine(rest: string): string | null {
  const line = rest.endsWith('\r') ? rest.slice(0, -1) : rest;
  return line.length === 0 ? null : line;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Отображение исключений транспорта на причины отказа.
 *
 * В Dart различались `TimeoutException`, `SocketException` и
 * `http.ClientException`. У `fetch` этого набора нет: таймаут выражается
 * отменой (её отмечает [timedOut]), а на DNS, отказ в соединении, оборванную
 * сеть и запрет CORS браузер отвечает одним `TypeError`.
 */
function connectFailure(error: unknown, timedOut: boolean): SseFailure {
  if (timedOut) return sseConnectTimeout(error);
  // Отмена не по таймауту приходит только от close(); её гасит защита в #fail.
  if (error instanceof TypeError || isAbortError(error)) {
    return sseUnreachable(error);
  }
  return sseBroken(error);
}

/**
 * Имя Dart-класса причины — в журнал шло `failure.runtimeType.toString()`.
 * `sseFailureToString` начинается именно с него, поэтому мапа имён остаётся
 * в одном месте (`sseFailure.ts`).
 */
function failureClassName(text: string): string {
  return text.split(' ')[0];
}

/** Заголовки запроса. Ничего не мутируется — собирается новый объект. */
function buildHeaders(
  headers: Readonly<Record<string, string>> | undefined,
  hasBody: boolean,
): Record<string, string> {
  return {
    Accept: 'text/event-stream',
    'Cache-Control': 'no-cache',
    ...headers,
    // Тело у нас всегда JSON. Пакет слал text/event-stream и на этом
    // спотыкались бекенды, отвечая 415.
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
  };
}

/**
 * Одно SSE-соединение.
 *
 * Заменяет flutter_http_sse, у которого было три проблемы: ошибкой считался
 * только статус 5xx, тело 4xx читалось как поток событий, и не было никакого
 * ограничения по времени — молчащий сервер вешал экран навсегда.
 */
export class SseConnection {
  private constructor(init: SseConnectionInit) {
    this.#onEvent = init.onEvent;
    this.#onFailure = init.onFailure;
    this.#onDone = init.onDone;
    this.#idleTimeout = init.idleTimeout;
    this.#connectTimeout = init.connectTimeout;
  }

  readonly #onEvent: (event: SseEvent) => void;
  readonly #onFailure: (failure: SseFailure) => void;
  readonly #onDone: () => void;

  readonly #idleTimeout: number;
  readonly #connectTimeout: number;

  /** Замена `http.Client`: отмена обрывает и запрос, и чтение тела. */
  readonly #abort = new AbortController();

  #reader: ReadableStreamDefaultReader<string> | null = null;
  #idleTimer: ReturnType<typeof setTimeout> | null = null;
  #closed = false;
  #finished = false;

  /** Отмена по таймауту соединения — чтобы отличить её от close(). */
  #connectTimedOut = false;

  /** Незавершённый хвост последнего куска — до следующего куска. */
  #pending = '';

  /** Строки текущего события, накопленные до пустой строки. */
  #buffer = '';

  /** Для журнала разработчика: что открывали и сколько ждали. */
  #title = '';
  #startedAt: number | null = null;
  #headersMs: number | undefined = undefined;

  #elapsedMs(): number | undefined {
    const startedAt = this.#startedAt;
    if (startedAt === null) return undefined;
    return Date.now() - startedAt;
  }

  /** Открывает поток. Возвращается сразу; события приходят в колбэки. */
  static open(options: SseConnectionOptions): SseConnection {
    const {
      url,
      onEvent,
      onFailure,
      onDone,
      method = DEFAULT_METHOD,
      body,
      headers,
      idleTimeout = DEFAULT_IDLE_TIMEOUT_MS,
      connectTimeout = DEFAULT_CONNECT_TIMEOUT_MS,
    } = options;

    const connection = new SseConnection({
      onEvent,
      onFailure: onFailure ?? (() => {}),
      onDone: onDone ?? (() => {}),
      idleTimeout,
      connectTimeout,
    });
    // Dart не ждал `_start`: соединение отдаётся сразу, всё остальное —
    // в колбэках.
    void connection.#start({
      url: typeof url === 'string' ? url : url.toString(),
      method,
      body,
      headers,
    });
    return connection;
  }

  async #start(request: SseRequest): Promise<void> {
    try {
      const reader = await this.#connect(request);
      if (reader === null) return;
      await this.#pump(reader);
    } catch (e) {
      // Сюда доходит только то, что не поймали #connect и #pump. Терять
      // такую ошибку молча нельзя: экран остался бы ждать ответа вечно.
      this.#fail(sseBroken(e));
    }
  }

  /**
   * Запрос и разбор заголовков. Возвращает читателя тела либо null, если
   * поток не открылся (отказ уже отдан в onFailure) или его закрыли.
   */
  async #connect(
    request: SseRequest,
  ): Promise<ReadableStreamDefaultReader<string> | null> {
    // Через прокси уходит завёрнутый адрес, в журнал пишется настоящий.
    const requestUrl = toRequestUrl(request.url);
    const logUrl = fromRequestUrl(requestUrl);
    const { method } = request;

    try {
      const hasBody = request.body !== undefined && request.body !== null;
      const init: RequestInit = {
        method,
        headers: buildHeaders(request.headers, hasBody),
        body: hasBody ? JSON.stringify(request.body) : undefined,
        signal: this.#abort.signal,
        cache: 'no-store',
      };

      logSse(`--> ${method} ${logUrl}`);
      this.#title = `${method} ${DiagnosticsLog.safeUrl(logUrl)}`;
      this.#startedAt = Date.now();
      const response = await this.#fetchHeaders(requestUrl, init);

      if (this.#closed) return null;

      const status = response.status;
      if (status < HTTP_OK_MIN || status >= HTTP_OK_MAX_EXCLUSIVE) {
        const text = await response.text();
        logSse(`<-- ${status} ${logUrl}`);
        this.#headersMs = this.#elapsedMs();
        this.#fail(
          status >= HTTP_SERVER_ERROR_MIN
            ? sseServerDown(status, text)
            : sseBadRequest(status, text),
        );
        return null;
      }

      logSse(`<-- ${status} ${logUrl}, поток открыт`);
      DiagnosticsLog.instance.stream({
        title: `${this.#title} — поток открыт`,
        code: String(status),
        durationMs: this.#elapsedMs(),
      });
      this.#armIdleTimer();
      return this.#openReader(response);
    } catch (e) {
      this.#fail(connectFailure(e, this.#connectTimedOut));
      return null;
    }
  }

  /**
   * `client.send(request).timeout(_connectTimeout)` из Dart.
   *
   * Таймаут ограничивает только ожидание заголовков: как только ответ пришёл,
   * таймер снимается, и молчание потока ловит уже [#armIdleTimer].
   */
  async #fetchHeaders(requestUrl: string, init: RequestInit): Promise<Response> {
    const timer = setTimeout(() => {
      this.#connectTimedOut = true;
      this.#abort.abort();
    }, this.#connectTimeout);
    try {
      return await fetch(requestUrl, init);
    } finally {
      clearTimeout(timer);
    }
  }

  #openReader(response: Response): ReadableStreamDefaultReader<string> | null {
    const stream = response.body;
    if (stream === null) {
      // Тела нет вовсе — читать нечего, поток кончился, не начавшись.
      this.#finish();
      return null;
    }
    const reader = stream.pipeThrough(new TextDecoderStream()).getReader();
    this.#reader = reader;
    return reader;
  }

  /** Замена подписки на `response.stream` из Dart. */
  async #pump(reader: ReadableStreamDefaultReader<string>): Promise<void> {
    try {
      while (!this.#closed && !this.#finished) {
        const { done, value } = await reader.read();
        if (this.#closed || this.#finished) return;
        if (done) {
          this.#flush();
          return;
        }
        this.#consume(value);
      }
    } catch (e) {
      // В Dart это `onError` подписки: обрыв посреди передачи — SseBroken.
      if (this.#closed || this.#finished) return;
      this.#fail(sseBroken(e));
    }
  }

  #consume(chunk: string): void {
    const { lines, rest } = takeLines(`${this.#pending}${chunk}`);
    this.#pending = rest;
    for (const line of lines) {
      this.#handleLine(line);
    }
  }

  /** Конец потока: хвост — это последняя строка, дальше — остаток события. */
  #flush(): void {
    const tail = flushLine(this.#pending);
    this.#pending = '';
    if (tail !== null) this.#handleLine(tail);
    if (this.#closed || this.#finished) return;
    if (this.#buffer.length > 0) {
      this.#onEvent(parseSseEvent(this.#buffer));
    }
    this.#finish();
  }

  /**
   * Одна строка тела. Пустая строка — конец события: накопленный блок
   * разбирается и отдаётся в onEvent.
   */
  #handleLine(line: string): void {
    if (this.#closed) return;
    this.#armIdleTimer();
    if (line.length === 0) {
      if (this.#buffer.length > 0) {
        const block = this.#buffer;
        this.#buffer = '';
        this.#onEvent(parseSseEvent(block));
      }
      return;
    }
    this.#buffer = this.#buffer.length > 0 ? `${this.#buffer}\n${line}` : line;
  }

  #armIdleTimer(): void {
    if (this.#idleTimer !== null) clearTimeout(this.#idleTimer);
    this.#idleTimer = setTimeout(() => {
      const seconds = Math.trunc(this.#idleTimeout / MS_PER_SECOND);
      logSse(`простой дольше ${seconds} с — закрываю поток`);
      this.#fail(sseSilent(this.#idleTimeout));
    }, this.#idleTimeout);
  }

  #fail(failure: SseFailure): void {
    if (this.#finished || this.#closed) return;
    this.#finished = true;
    const text = sseFailureToString(failure);
    logSse(`ошибка: ${text}`);
    DiagnosticsLog.instance.stream({
      title: `${this.#title} — ошибка`,
      code: failureClassName(text),
      durationMs: this.#headersMs ?? this.#elapsedMs(),
      details: text,
      isError: true,
    });
    this.#teardown();
    this.#onFailure(failure);
  }

  #finish(): void {
    if (this.#finished || this.#closed) return;
    this.#finished = true;
    this.#teardown();
    this.#onDone();
  }

  #teardown(): void {
    if (this.#idleTimer !== null) clearTimeout(this.#idleTimer);
    this.#idleTimer = null;
    const reader = this.#reader;
    this.#reader = null;
    if (reader !== null) {
      // cancel() отклоняется, если поток уже оборвался, — это не ошибка.
      void reader.cancel().catch(() => undefined);
    }
    // В Dart здесь закрывался `http.Client`.
    this.#abort.abort();
  }

  /** Закрывает поток по инициативе приложения. Колбэки больше не вызываются. */
  close(): void {
    if (this.#closed) return;
    this.#closed = true;
    this.#teardown();
  }
}
